import * as React from "react"
import cx from "classnames"
import { Link, graphql } from "gatsby"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS } from "@contentful/rich-text-types"
import { formatDate } from "../components/formatDate"
import BookDemoLink from "../components/BookDemoLink"
import Layout from "../components/layout"
import Seo from "../components/seo"

const topButtonsStyle =
  "rounded-header h-20 tablet:h-32 desktop:h-44 w-3/5 mobile:w-[200px] tablet:w-[330px] desktop:w-[482px]"

const colorBlocks = [
  ["bg-green-300", "bg-green-200", "bg-green-100"],
  ["bg-blue-300", "bg-blue-200", "bg-blue-100"],
  ["bg-red-300", "bg-red-200", "bg-red-100"],
  ["bg-purple-300", "bg-purple-200", "bg-purple-100"],
  ["bg-yellow-300", "bg-yellow-200", "bg-yellow-100"],
]

const labelColors = [
  "text-green-400",
  "text-blue-400",
  "text-red-400",
  "text-purple-400",
  "text-yellow-400",
]

const options = references => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const isCode = node.content.some(content =>
        content.marks?.some(mark => mark.type === "code")
      )
      if (isCode) {
        return <span className="language-highlight">{children}</span>
      }
      return <p>{children}</p>
    },
    [BLOCKS.TABLE]: (node, children) => {
      return (
        <div className="max-w-full overflow-x-auto">
          <table>{children}</table>
        </div>
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
      const asset = references.find(
        ({ contentful_id }) => contentful_id === node.data.target.sys.id
      )

      if (asset) {
        return (
          <img
            src={asset.file.url}
            alt={asset.title}
            className="article-img my-10"
          />
        )
      }

      return null
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const entry = references.find(
        ({ contentful_id }) => contentful_id === node.data.target.sys.id
      )
      if (entry?.__typename === "ContentfulPodcastPlayer") {
        return (
          <div className="w-full h-[250px] mb-10 rounded-md overflow-hidden">
            <iframe
              title={entry.title}
              className="podcast"
              frameborder="no"
              scrolling="no"
              allow="clipboard-write"
              seamless
              src={entry.url}
            />
          </div>
        )
      }
      if (entry?.__typename === "ContentfulCodeBlock") {
        const richText = JSON.parse(entry.code.raw)
        return (
          <pre className="language-sql language-highlight">
            {richText.content.flatMap(content => {
              return content.content.map(content => {
                return (
                  <SyntaxHighlighter language="sql">
                    {content.value}
                  </SyntaxHighlighter>
                )
              })
            })}
          </pre>
        )
      }
      if (entry?.__typename === "ContentfulYoutubePlayer") {
        return (
          <div className="my-10">
            <iframe
              title={entry.title}
              width="990"
              height="559"
              src={entry.url}
              frameborder="no"
              scrolling="no"
              allow="clipboard-write"
              seamless
            />
          </div>
        )
      }

      return null
    },
  },
})

const RichTextResponse = ({ richText, references }) => (
  <>{documentToReactComponents(richText, options(references))}</>
)

// overflow-x-auto article-content h2, article-content h2 a .language-text, article-content ul, article-content li
// article-content p, article-content img, article-content iframe, article-content table, article-content .gatsby-resp-image-wrapper
// article-content th, article-content td, article-content code.language-text, article-content .gatsby-highlight
// .article-content .gatsby-highlight, pre[class*="language-"], :not(pre) > code[class*="language-"], pre[class*="language-"],
// code[class*="language-"], pre[class*="language-"], code[class*="language-"], pre[class*="language-"],
// .token.atrule, .token.attr-value, .token.keyword, .token.punctuation
// .token.operator, .token.entity, .token.url, .language-css .token.string, .style .token.string
// .token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted
// .token.property, .token.tag, .token.boolean, .token.number, .token.constant, .token.symbol, .token.deleted

const BlogPostTemplate = ({
  data: { allContentfulBlogPost, site, contentfulBlogPost },
  location,
}) => {
  const siteTitle = site.siteMetadata?.title || `Title`
  const posts = allContentfulBlogPost.edges.map(edge => edge.node)
  const post = contentfulBlogPost

  const shuffledColorBlock = colorBlocks
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)[0]

  const shuffeledLabelColors = labelColors
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)

  const richText = JSON.parse(post.richText.raw)

  return (
    <Layout location={location} title={siteTitle}>
      <article
        className="bg-white"
        itemScope
        itemType="http://schema.org/Article"
      >
        <header className="pt-20 mobile:pt-40 tablet:pt-56 pb-16 tablet:pb-28 desktop:pb-24">
          <div className="flex flex-col justify-center container-thin">
            <div className="mobile:flex justify-center mobile:justify-start m-auto mobile:m-0 mobile:space-x-0 items-center">
              <div className="flex flex-col items-center mobile:hidden">
                {shuffledColorBlock.map((color, index) => (
                  <div
                    key={`color-block-${index}`}
                    className={cx(
                      topButtonsStyle,
                      index % 2 === 0
                        ? "ml-16 mobile:ml-[100px]"
                        : "mr-16 mobile:mr-0",
                      color
                    )}
                  />
                ))}
              </div>
              <div className="container-thin px-6 pt-8 mobile:pt-0 desktop:px-0">
                <div className="flex items-center space-x-4 mb-6">
                  {post.tags?.map((tag, index) => (
                    <React.Fragment key={`tag-${index}`}>
                      <Link
                        to={`/blog/?filter=${tag.title}`}
                        className={cx(
                          "text-base font-mono",
                          shuffeledLabelColors[index]
                        )}
                      >
                        {tag.title}
                      </Link>
                      {index < post.tags?.length - 1 && (
                        <div className="w-1 h-1 mt-1 bg-black rounded-full" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <h1 className="text-mobile-title mobile:text-6xl font-semibold leading-[1.1] desktop:mb-6">
                  {post.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-6 text-sm-mobile desktop:text-base font-mono">
                  <span className="shrink-0 text-nowrap">
                    {formatDate(post.date)}
                  </span>
                  {post.author?.length > 0 && (
                    <>
                      <span>|</span>
                      <span className="shrink-0 text-nowrap">
                        {post.author.map(author => author.name).join(", ")}
                      </span>
                    </>
                  )}
                  {post.minToRead && (
                    <>
                      <span>|</span>
                      <span className="shrink-0 text-nowrap">
                        {post.minToRead}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        <section className="px-6 desktop:px-0 container-thin text-xl article-content-blog">
          <RichTextResponse
            richText={richText}
            references={post.richText.references}
          />
        </section>
      </article>

      <div className="px-6 desktop:px-0">
        {/* <div
          className={cx(
            shuffledColorBlock[2],
            "container-thin mt-10 p-6 tablet:mt-28 rounded-3xl tablet:py-10 tablet:px-16"
          )}
        >
          <p className="text-2xl tablet:text-3xl mb-16 tablet:mb-32">
            Like our post?
            <br />
            You will like the product more!
          </p>
          <div className="mobile:flex items-center space-y-6 mobile:space-y-0 mobile:space-x-6">
            <Link
              aria-label="Go to Contact Us page"
              to="/contact-us"
              className="btn-transparent-black w-full flex-1 h-16"
            >
              Contact us
            </Link>
            <BookDemoLink className="btn-black w-full flex-1 h-16" />
          </div>
        </div> */}

        <div className="pt-12 container pb-16">
          <div className="flex items-center justify-center mobile:justify-between mb-6 tablet:mb-12">
            <h2 className="text-2xl tablet:text-3xl">You might also like</h2>
            <Link
              className="hidden mobile:flex btn-transparent-black text-sm w-40 h-12"
              to="/blog"
              itemProp="url"
              aria-label="Go to Blog page"
            >
              View All Posts
            </Link>
          </div>

          <ol className="mobile:grid grid-cols-3 gap-x-6">
            {posts.map(post => {
              const title = post.title || post.slug
              const previewImg = getImage(post.previewImg)
              return (
                <li key={post.slug}>
                  <Link
                    to={`/blog/${post.slug}`}
                    aria-label={`Learn about ${title}`}
                  >
                    <article itemScope itemType="http://schema.org/Article">
                      <header>
                        {previewImg && (
                          <GatsbyImage
                            className="w-full aspect-[1.6] mb-6 rounded-tab overflow-hidden"
                            image={previewImg}
                            alt={title}
                          />
                        )}
                        <small className="font-mono text-sm-mobile tablet:text-base">
                          {formatDate(post.date)}
                        </small>
                        <h2 className="line-clamp-2 text-2xl tablet:text-3xl mt-4 mb-10 tablet:mt-7 tablet:mb-5">
                          {title}
                        </h2>
                      </header>
                    </article>
                  </Link>
                </li>
              )
            })}
          </ol>
          <Link
            className="mobile:hidden btn-transparent-black text-base w-full h-12"
            to="/blog"
            aria-label="Go to Blog page"
            itemProp="url"
          >
            View All Posts
          </Link>
        </div>
      </div>
    </Layout>
  )
}

export const Head = data => {
  const post = data.data.contentfulBlogPost
  return <Seo title={post.title} description={post.description} />
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    site {
      siteMetadata {
        title
      }
    }
    contentfulBlogPost(slug: { eq: $slug }) {
      title
      id
      tags {
        title
      }
      date
      minToRead
      description
      richText {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            __typename
            url
            title
            description
            file {
              url
              fileName
              contentType
            }
          }
          ... on ContentfulPodcastPlayer {
            contentful_id
            __typename
            url
            title
          }
          ... on ContentfulYoutubePlayer {
            contentful_id
            __typename
            url
            title
          }
          ... on ContentfulCodeBlock {
            contentful_id
            __typename
            code {
              raw
            }
          }
        }
      }

      previewImg {
        gatsbyImageData(width: 800, placeholder: BLURRED)
      }
      slug
      author {
        name
      }
    }
    allContentfulBlogPost(sort: { date: DESC }, limit: 3) {
      edges {
        node {
          title
          id
          date
          description
          previewImg {
            gatsbyImageData(width: 800, placeholder: BLURRED)
          }
          slug
          tags {
            title
          }
        }
      }
    }
  }
`
